'use client';

import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, FileText, Plus, Trash2, Pencil, Code, Copy, Check, Phone } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import {
  conversionActionsQueryOptions,
  conversionTrackingSnippetQueryOptions,
  useRemoveConversionMutation,
  useUpdateConversionMutation,
} from '@/lib/query-options';
import { useToast } from '@/hooks/useToast';
import { googleAdsAccountAtom } from '@/store';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { NoGoogleAdsAccountSelected } from '@/components/glitch-ui/no-google-ads-account';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { ConversionAction } from '@/lib/types';
import { DataTable } from '@/components/glitch-ui/data-table';
import { CreateConversionForm } from '@/components/settings/create-conversion-form';

export function ConversionManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const { symbol } = useCurrencySymbol();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Fetch conversion actions
  const { data: conversionActionsData, isLoading, isError, refetch } = useQuery(conversionActionsQueryOptions());
  const conversionActions = conversionActionsData?.conversion_actions || [];

  // Refetch conversion actions when Google Ads account changes
  useEffect(() => {
    if (googleAdsAccount?.googleads_account_id) {
      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
    }
  }, [googleAdsAccount?.googleads_account_id, queryClient]);

  // Mutations
  const removeConversionMutation = useRemoveConversionMutation();
  const updateConversionMutation = useUpdateConversionMutation();

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    conversionId: string;
    conversionName: string;
  }>({ isOpen: false, conversionId: '', conversionName: '' });

  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingConversion, setEditingConversion] = useState<ConversionAction | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editValue, setEditValue] = useState<number | undefined>();
  const [editPrimaryForGoal, setEditPrimaryForGoal] = useState<boolean | undefined>();
  const [editFunnelStage, setEditFunnelStage] = useState<string>('');

  // Tracking code dialog state
  const [showTrackingCodeDialog, setShowTrackingCodeDialog] = useState(false);
  const [trackingCodeConversion, setTrackingCodeConversion] = useState<ConversionAction | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { data: trackingSnippetData, isLoading: isLoadingSnippet } = useQuery(
    conversionTrackingSnippetQueryOptions(showTrackingCodeDialog ? (trackingCodeConversion?.id ?? null) : null)
  );

  const handleRemoveConversion = async () => {
    if (!deleteModal.conversionId) return;

    try {
      await removeConversionMutation.mutateAsync(deleteModal.conversionId);
      setDeleteModal({ isOpen: false, conversionId: '', conversionName: '' });
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleOpenEditDialog = (conversion: ConversionAction) => {
    setEditingConversion(conversion);
    setEditName(conversion.name || '');
    setEditCategory(conversion.category || 'DEFAULT');
    setEditValue(conversion.default_value ?? undefined);
    setEditPrimaryForGoal(conversion.primary_for_goal ?? undefined);
    setEditFunnelStage(conversion.funnel_stage || '');
    setShowEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditingConversion(null);
    setEditName('');
    setEditCategory('');
    setEditValue(undefined);
    setEditPrimaryForGoal(undefined);
    setEditFunnelStage('');
  };

  const handleOpenTrackingCodeDialog = (conversion: ConversionAction) => {
    setTrackingCodeConversion(conversion);
    setShowTrackingCodeDialog(true);
  };

  const handleCloseTrackingCodeDialog = () => {
    setShowTrackingCodeDialog(false);
    setTrackingCodeConversion(null);
    setCopiedField(null);
  };

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateConversion = async () => {
    if (!editingConversion) return;

    try {
      await updateConversionMutation.mutateAsync({
        conversionActionId: editingConversion.id,
        request: {
          name: editName || undefined,
          category: editCategory || undefined,
          value: editValue,
          primary_for_goal: editPrimaryForGoal,
          funnel_stage: editFunnelStage || undefined,
        },
      });
      handleCloseEditDialog();
    } catch (error) {
      // Error handled by mutation
    }
  };

  // Format conversion type
  const formatConversionType = (type: string | null | undefined): string | null => {
    const labels: Record<string, string> = {
      WEBPAGE: 'Website',
      PHONE_CALL: 'Phone Call',
      UPLOAD_CLICKS: 'Offline',
      UPLOAD_CALLS: 'Offline Call',
    };
    return type ? (labels[type] ?? null) : null;
  };

  // Column definitions for DataTable
  const columns: ColumnDef<ConversionAction, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const conversion = row.original;
        return (
          <div>
            <div className="text-sm font-medium text-gray-900">{conversion.name || '-'}</div>
            {formatConversionType(conversion.type) && (
              <div className="text-sm font-normal text-gray-500">{formatConversionType(conversion.type)}</div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: ({ row }) => {
        const source = row.original.source;
        const imgSourceConfig: Record<string, { label: string; icon: string }> = {
          GOOGLE_TAG: { label: 'Google Tag', icon: '/google-ads.svg' },
          HUBSPOT: { label: 'HubSpot', icon: '/hubspot.png' },
          META: { label: 'Meta', icon: '/meta.png' },
        };
        if (source === 'PHONE_CALL') {
          return (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-900">Phone Call</span>
            </div>
          );
        }
        const config = source ? imgSourceConfig[source] : null;
        return (
          <div className="flex items-center gap-2">
            {config ? (
              <>
                <img src={config.icon} alt="" className="h-4 w-4 object-contain" />
                <span className="text-sm text-gray-900">{config.label}</span>
              </>
            ) : (
              <span className="text-sm text-gray-500">Unknown</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'funnel_stage',
      header: 'Stage',
      cell: ({ row }) => {
        const stage = row.original.funnel_stage;
        const stageConfig: Record<string, { label: string; className: string }> = {
          LEAD: { label: 'Lead', className: 'bg-gray-100 text-gray-900' },
          MQL: { label: 'MQL', className: 'bg-blue-100 text-blue-800' },
          SQL: { label: 'SQL', className: 'bg-amber-100 text-amber-800' },
          CUSTOMER: { label: 'Customer', className: 'bg-green-100 text-green-800' },
        };
        const config = stage ? stageConfig[stage] : null;
        return config ? (
          <span className={`inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
        ) : (
          <span className="text-sm font-medium text-gray-900">-</span>
        );
      },
    },
    {
      accessorKey: 'primary_for_goal',
      header: 'Goal',
      cell: ({ row }) => {
        const conversion = row.original;
        return conversion.primary_for_goal === true ? (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Primary
          </span>
        ) : conversion.primary_for_goal === false ? (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
            Secondary
          </span>
        ) : (
          <span className="text-sm font-medium text-gray-900">-</span>
        );
      },
    },
    {
      accessorKey: 'default_value',
      header: 'Value',
      cell: ({ row }) => {
        const conversion = row.original;
        return (
          <div className="text-sm font-medium text-gray-900">
            {conversion.default_value !== null && conversion.default_value !== undefined
              ? `${symbol}${conversion.default_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : '-'}
          </div>
        );
      },
    },
    {
      accessorKey: 'conversions_last_30d',
      header: 'Conv',
      cell: ({ row }) => {
        const val = row.original.conversions_last_30d;
        return val != null && val > 0
          ? <span className="text-sm text-gray-900">{val.toLocaleString()}</span>
          : <span className="text-sm text-gray-400">—</span>;
      },
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      cell: ({ row }) => {
        const conversion = row.original;
        return (
          <div className="flex items-center justify-end gap-1">
            {conversion.type === 'WEBPAGE' && (
              <IconButton
                aria-label="View tracking code"
                onClick={() => handleOpenTrackingCodeDialog(conversion)}
              >
                <Code className="h-4 w-4" />
              </IconButton>
            )}
            <IconButton
              aria-label="Edit conversion"
              onClick={() => handleOpenEditDialog(conversion)}
            >
              <Pencil className="h-4 w-4" />
            </IconButton>
            <IconButton
              variant="destructive"
              aria-label="Delete conversion"
              onClick={() =>
                setDeleteModal({
                  isOpen: true,
                  conversionId: conversion.id,
                  conversionName: conversion.name || 'Unnamed conversion',
                })
              }
            >
              <Trash2 className="h-4 w-4" />
            </IconButton>
          </div>
        );
      },
    },
  ];

  // No Google Ads account selected
  if (!googleAdsAccount?.googleads_account_id) {
    return (
      <NoGoogleAdsAccountSelected description="A Google Ads account needs to be selected for this organization before conversion actions can be managed." />
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="bg-white shadow-sm border border-red-200 rounded-lg overflow-hidden">
        <div className="p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">Failed to load conversion actions</span>
        </div>
      </div>
    );
  }

  // Loading state: only on the true first load (no cached data yet). A background
  // refetch of already-cached data (mount re-fetch, window-focus, invalidation) keeps
  // the current data on screen instead of blanking the whole page to a spinner — that
  // full-page blank on every navigation was the "loader flash" (#5342).
  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Conversion Tracking</h2>
            <p className="text-sm text-gray-600 mt-1">Track actions visitors take on your website.</p>
          </div>
        </div>
        <div className="border rounded-lg bg-white overflow-hidden">
          <div className="flex items-center justify-center gap-3 py-16">
            <Spinner size="md" className="text-gray-500" />
            <span className="text-gray-500">Loading conversion actions...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Conversion Tracking</h2>
          <p className="text-sm text-gray-600 mt-1">Track actions visitors take on your website.</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" className="text-gray-900" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Conversion
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Conversion Action</DialogTitle>
            </DialogHeader>
            <CreateConversionForm
              onCreated={() => {
                refetch();
                setShowCreateDialog(false);
              }}
              onCancel={() => setShowCreateDialog(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Empty state — matches the inline ``min-h-[50vh]`` pattern used by
          Landing Pages so the section doesn't collapse into a tiny placeholder
          row. The header's Add Conversion button stays visible as the CTA. */}
      {conversionActions.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
          <FileText className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
          <h3 className="text-lg font-semibold text-gray-500 mb-2">No Conversion Actions</h3>
          <p className="text-gray-500">Create your first conversion action to start tracking conversions</p>
        </div>
      ) : (
        <DataTable<ConversionAction>
          data={conversionActions}
          columns={columns}
          variant="flush"
          pageSize={999}
        />
      )}

      {/* Confirmation Modal for removing conversion actions */}
      <ConformationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, conversionId: '', conversionName: '' })}
        onConfirm={handleRemoveConversion}
        title="Remove Conversion Action"
        description={`Are you sure you want to remove "${deleteModal.conversionName}"? This will set the conversion action status to removed in Google Ads.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={removeConversionMutation.isPending}
      />

      {/* Edit Conversion Dialog — source-aware */}
      <Dialog open={showEditDialog} onOpenChange={(open) => !open && handleCloseEditDialog()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingConversion?.source === 'HUBSPOT' && <img src="/hubspot.png" alt="" className="h-5 w-5" />}
              {editingConversion?.source === 'GOOGLE_TAG' && <img src="/google-ads.svg" alt="" className="h-5 w-5" />}
              Edit Conversion Action
            </DialogTitle>
            <DialogDescription>
              {editingConversion?.source === 'HUBSPOT'
                ? 'Update this HubSpot conversion action.'
                : 'Update the conversion action settings.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Conversion name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-funnel-stage">Stage</Label>
                <Select value={editFunnelStage} onValueChange={setEditFunnelStage}>
                  <SelectTrigger id="edit-funnel-stage">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LEAD">Lead</SelectItem>
                    <SelectItem value="MQL">MQL</SelectItem>
                    <SelectItem value="SQL">SQL</SelectItem>
                    <SelectItem value="CUSTOMER">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-value">Conversion Value</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{symbol}</span>
                  <Input
                    id="edit-value"
                    type="number"
                    step="0.01"
                    value={editValue ?? ''}
                    onChange={(e) =>
                      setEditValue(e.target.value ? parseFloat(e.target.value) : undefined)
                    }
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-channel">Channel</Label>
                <Select disabled value={editingConversion?.type === 'PHONE_CALL' ? 'call' : 'website'}>
                  <SelectTrigger id="edit-channel" className="disabled:opacity-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-primary">Conversion Type</Label>
                <Select
                  value={editPrimaryForGoal === true ? 'primary' : editPrimaryForGoal === false ? 'secondary' : ''}
                  onValueChange={(v) => setEditPrimaryForGoal(v === 'primary' ? true : v === 'secondary' ? false : undefined)}
                >
                  <SelectTrigger id="edit-primary">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {editingConversion?.type === 'PHONE_CALL' && (
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input value={editingConversion?.phone_number || ''} disabled className="disabled:opacity-50" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <SaveChangesButton
              onClick={handleUpdateConversion}
              saving={updateConversionMutation.isPending}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tracking Code Dialog */}
      <Dialog open={showTrackingCodeDialog} onOpenChange={(open) => !open && handleCloseTrackingCodeDialog()}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tracking Code</DialogTitle>
            <DialogDescription>
              Install these codes on your website to track "{trackingCodeConversion?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {isLoadingSnippet ? (
              <div className="flex items-center justify-center gap-3 py-8">
                <Spinner size="md" className="text-gray-500" />
                <span className="text-sm text-gray-500">Loading tracking code...</span>
              </div>
            ) : (
              <>
                {/* Global Site Tag */}
                {trackingSnippetData?.tracking_snippet?.global_site_tag && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm font-medium">Global Site Tag (gtag.js)</Label>
                        <p className="text-xs text-gray-500 mt-0.5">Include this in the {'<head>'} section on every page of your website.</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyToClipboard(trackingSnippetData.tracking_snippet!.global_site_tag!, 'global_site_tag')}
                      >
                        {copiedField === 'global_site_tag' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                        Copy
                      </Button>
                    </div>
                    <pre className="bg-gray-100 p-3 rounded text-xs font-mono whitespace-pre-wrap break-all">
                      {trackingSnippetData.tracking_snippet.global_site_tag}
                    </pre>
                  </div>
                )}

                {/* Event Snippet */}
                {trackingSnippetData?.tracking_snippet?.event_snippet && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm font-medium">Event Snippet</Label>
                        <p className="text-xs text-gray-500 mt-0.5">Add to your conversion page (e.g., thank you page after sign-up).</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyToClipboard(trackingSnippetData.tracking_snippet!.event_snippet!, 'event_snippet')}
                      >
                        {copiedField === 'event_snippet' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                        Copy
                      </Button>
                    </div>
                    <pre className="bg-gray-100 p-3 rounded text-xs font-mono whitespace-pre-wrap break-all">
                      {trackingSnippetData.tracking_snippet.event_snippet}
                    </pre>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseTrackingCodeDialog}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
